import type { Transition, Variants } from "motion/react";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { tokens } from "@/theme/tokens";

const { duration, easing } = tokens.motion;

/** Shared entry animation for messages and surfaces: a subtle rise + fade.
 *  One source of truth so motion is consistent and centrally tunable. */
export const entryVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
};

/** Page-content transition (workspace route swaps): a slightly larger,
 *  softer rise so navigating between pages feels deliberate, not abrupt. */
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

/** Spring used for tactile control feedback (send button press, etc.). */
export const tactileSpring: Transition = { type: "spring", stiffness: 500, damping: 30 };

/** Named CSS transitions for hover/focus micro-interactions, sourced from the
 *  motion tokens. Use these instead of inline `transition: "... 180ms ease"`. */
export const cssTransition = {
  /** General color/opacity/background hover feedback. */
  hover: `color ${duration.fast}ms ease, background-color ${duration.fast}ms ease, border-color ${duration.fast}ms ease, opacity ${duration.fast}ms ease`,
  /** Lift + shadow for interactive cards. */
  lift: `transform ${duration.normal}ms cubic-bezier(${easing.bounce.join(
    ",",
  )}), box-shadow ${duration.normal}ms ease`,
} as const;

/** Resolve the transition for entry/exit motion. When the user prefers
 *  reduced motion we collapse to an instant transition AND set distance to 0
 *  at the call site (via `reduce`), so nothing moves and nothing fades-in
 *  with travel. */
export function resolveTransition(reducedMotion: boolean): Transition {
  if (reducedMotion) return { duration: 0 };
  return { duration: duration.normal / 1000, ease: easing.smooth };
}

/** One-line, ellipsized label for a `neuTriggerSx` button. Pair it with a
 *  wrapping span (a text node can't ellipsize — it isn't a flex item), so a
 *  long project name clips instead of wrapping onto a second line and
 *  colliding with the toolbar's action buttons. */
export const neuTriggerLabelSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/**
 * Ghost dropdown-trigger style for labeled menu buttons in the composer toolbar
 * (model picker, project badge). At rest it's a quiet, fill-less pill — just a
 * muted glyph + label on the tinted surface. Hover and the `open` menu state
 * bloom into the accent tint (accent-colored text on an accent @ 12% fill), so
 * the trigger speaks the same accent language as the ghost icon buttons. Honors
 * `prefers-reduced-motion`.
 */
export function neuTriggerSx(open: boolean): SxProps<Theme> {
  return (t: Theme) => ({
    color: open ? "primary.main" : "text.secondary",
    fontWeight: 600,
    // Shrinkable to nothing: the composer toolbar has to survive a narrow
    // embed panel, where these chips must give up width to the action
    // cluster rather than push it off the row.
    minWidth: 0,
    maxWidth: "100%",
    flexShrink: 1,
    px: 1,
    py: 0.5,
    borderRadius: 2,
    bgcolor: open ? alpha(t.palette.primary.main, 0.12) : "transparent",
    boxShadow: "none",
    transition: `background-color ${duration.fast}ms ease, color ${duration.fast}ms ease`,
    "&:hover": {
      color: "primary.main",
      bgcolor: alpha(t.palette.primary.main, 0.12),
    },
    "&:active": { bgcolor: alpha(t.palette.primary.main, 0.18) },
    // Glyphs keep their size while the label absorbs the squeeze.
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { flexShrink: 0 },
    // The chevron nudges down a hair when the menu is open — a small,
    // legible "expanded" cue.
    "& .MuiButton-endIcon": {
      transition: `transform ${duration.fast}ms ease`,
      transform: open ? "translateY(1px) rotate(180deg)" : "none",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "background-color 160ms ease, color 160ms ease",
      "& .MuiButton-endIcon": { transition: "none" },
    },
  });
}
